import type { AccountAccessPayload } from "@dealer-center/ddc-pages-configuration-interface";

/**
 * Whether an account hierarchy applies to the subject — the condition FR-006 gates the tab on.
 *
 * **"Any nodes at all" is the wrong test.** `accountaccess` returns the subject's own account even
 * when there is nothing above or below it, so a single-account user gets exactly one node back. Showing
 * an "Account Access" tab that lists one row — the account whose name is already in the page header —
 * is the empty-ish tab FR-006 exists to remove. A hierarchy applies when there is something to
 * *nest*: more than one root, or a root with children.
 *
 * `undefined` means the query has not resolved yet and is deliberately **not** the same as "no
 * hierarchy": per research R-007 the tab renders immediately in a loading state and is removed only
 * once the query resolves. The caller must distinguish pending from resolved-and-false; this function
 * only answers the resolved question.
 *
 * Recorded for whoever revisits this: for `dealerdemo/jeffersonh7` the live payload is 37 top-level
 * nodes (49 including `children`), so the reference subject is comfortably on the "applies" side. The
 * single-account case is untested against live data — no such nonprod subject was sampled — which is
 * why the rule is stated here explicitly rather than left implicit in a `.length` check.
 */
export const hasAccountHierarchy = (
  payload: AccountAccessPayload | undefined
): boolean => {
  const roots = payload?.accountPermissions;

  if (!roots || roots.length === 0) {
    return false;
  }

  /*
   * `children` is optional (the swagger omits it and nothing validates the payload), and this runs in
   * `MyInfo`'s render body — outside every `TabState`. An unguarded `.length` on an absent `children`
   * would throw to the app-level `ErrorBoundary` and take down the entire page rather than just this
   * tab, so a missing `children` must read as "no children", never as an exception.
   */
  return (
    roots.length > 1 || roots.some(root => (root.children ?? []).length > 0)
  );
};
